import { RealmRoles } from 'constants/index';
import { EyeOutlined, ToolOutlined } from '@ant-design/icons';
import React, { useMemo, useState } from 'react';

import dayjs, { Dayjs } from 'dayjs';

import useGetBalances from 'hooks/useGetBalance';
import useGetTransactions from 'hooks/useGetTransactions';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Row,
  Space,
  Statistic,
  Table,
  Typography,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import PageWithFilters from 'components/page/page-with-filters';
import { PageWithFiltersContentContainer } from 'components/page/page.styles';
import { ITransaction } from 'interfaces/transactions';
import { getProperDate } from 'utils/date';
import config from 'config';
import { CardHoldersActionsContainer } from '../features/card-holders/list/card-holders.styles';
import useGetCsvFile from '../hooks/useGetCsvFile';
import ButtonExport from '../components/btn-export/button-export';
import useToastContext from '../hooks/useToastContext';
import withAuthorization from '../components/hocs/withAuthorization';
import { TransactionHelper } from './transactions/helper/transactions.helper';
import TransactionStateTag from './transactions/components/transaction-state-tag';

const { RangePicker } = DatePicker;

export const SIGN_BY_TYPE_OF_TRANSACTION = new Map(
  Object.entries({
    CASH_OUT: '-',
    LOAD: '+',
    PREPAID_REVERSE: '-',
    PURCHASE: '-',
    COMPLETION: '+',
    REVERSAL: '+',
    TRANSFER_CREDIT:  '+',
    TRANSFER_DEBIT:  '-',
    TRANSFER: '+'
  })
);
const SUM_BY_TYPE_OF_TRANSACTION = new Map(
  Object.entries({
    CASH_OUT: false,
    LOAD: true,
    PREPAID_REVERSE: false,
    COMPLETION: true,
    PURCHASE: false,
    REVERSAL: true,
    TRANSFER_CREDIT: true,
    TRANSFER_DEBIT: false,
    TRANSFER: true
  })
);

type SearchTransactionsFilters = {
  dateRanges: [Dayjs, Dayjs];
};

const TODAY_DATE = new Date();
const LAST_MONTH_DATE = new Date(new Date().setMonth(TODAY_DATE.getMonth() - 1));

export const TransactionsFilters = ({ onSubmit }: { onSubmit: (data: SearchTransactionsFilters) => void }) => {
  const [form] = Form.useForm<SearchTransactionsFilters>();

  const formStyle: React.CSSProperties = {
    maxWidth: 'none',
  };

  const onFinish = (values: SearchTransactionsFilters) => {
    onSubmit(values);
  };

  const onReset = () => {
    // @ts-ignore
    onSubmit({});
  };

  return (
    <Form form={form} name="advanced_search" style={formStyle} onFinish={onFinish} onReset={onReset}>
      <Row gutter={24}>
        <Col span={8}>
          <Form.Item<SearchTransactionsFilters> name="dateRanges" label="Fechas">
            <RangePicker style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={8}></Col>
        <Col span={8}>
          <div style={{ textAlign: 'right' }}>
            <Space size="small">
              <Button type="primary" htmlType="submit">
                Buscar
              </Button>
              <Button htmlType="reset">Limpiar</Button>
            </Space>
          </div>
        </Col>
      </Row>
    </Form>
  );
};

const TransactionsPage = () => {
  const toast = useToastContext();
  const { cardId, issuer } = useParams<{ cardId: string; issuer: string }>();
  const navigate = useNavigate();
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const [startDate, setStartDate] = useState<Dayjs>(dayjs(LAST_MONTH_DATE));
  const [endDate, setEndDate] = useState<Dayjs>(dayjs(TODAY_DATE));

  const { transactions, isLoading, pageInfo } = useGetTransactions(
    cardId,
    startDate.format('YYYY-MM-DDT00:00:00').toString(),
    endDate.format('YYYY-MM-DDT23:59:59').toString(),
    paginationModel.pageSize,
    paginationModel.page
  );
  const { balance } = useGetBalances(cardId);

  const { fileLoading, execute } = useGetCsvFile(`v1/reports`);

  const debitsAndCredits = useMemo(() => {
    return transactions?.reduce(
      (acc, trx) => {
        const amount = trx.authorization.info.amounts.original.amount;
        if (SUM_BY_TYPE_OF_TRANSACTION.get(trx.authorization.info.transaction.type)) {
          acc.credits += amount;
        } else {
          acc.debits += amount;
        }
        return acc;
      },
      { debits: 0, credits: 0 }
    );
  }, [endDate, startDate, cardId, isLoading, transactions]);

  const handleSearchClick = (data: SearchTransactionsFilters) => {
    setStartDate(data.dateRanges?.[0] ?? dayjs(LAST_MONTH_DATE));
    setEndDate(data.dateRanges?.[1] ?? dayjs(TODAY_DATE));
  };

  const onClickViewTransactionDetails = (trxId: string, cardHolderId: string, date: string, transactionType: string) => {
    navigate(`/${issuer}/transactions/${cardId}/${trxId}?cardHolderId=${cardHolderId}&date=${date}&transactionType=${transactionType}`);
  };

  const handlePaginationChange = (page: number, pageSize: number) => {
    setPaginationModel({ page: page - 1, pageSize });
  };


  const getDateByTransactionType = (transaction: ITransaction) => {
    return getProperDate(transaction?.authorization?.info?.transaction?.received_timestamp).concat('hs')
  }


  const getAmountColor = (transaction: ITransaction) => {
    let color = '#000000'

    if(transaction?.authorization?.approved || transaction?.authorization?.irc === '8444'){
      color = SIGN_BY_TYPE_OF_TRANSACTION.get(transaction.authorization.info.transaction.type) === '+' ? '#52c41a' : '#ff4d4f'
    }


    return color
  }


  const columns: ColumnsType<ITransaction> = [
    {
      dataIndex: 'received_timestamp',
      key: 'received_timestamp',
      title: 'Fecha',
      render: (_, record) => {
        return getDateByTransactionType(record);
      },
    },
    {
      dataIndex: 'merchant_name',
      key: 'merchant_name',
      title: 'Descripción',
      render: (_, record) => {
        return TransactionHelper.getMerchant(record.authorization.info.transaction.type, record.authorization.info.merchant.name)
      },
    },
    {
      dataIndex: 'final_amount',
      key: 'final_amount',
      title: 'Importe',
      render: (_, record) => {
        return (
          <Typography.Text  strong style={{color: getAmountColor(record)}}>
            {
              record?.authorization?.approved || record?.authorization?.irc === '8444' ? SIGN_BY_TYPE_OF_TRANSACTION.get(record.authorization.info.transaction.type) : ''
            }
            { TransactionHelper.getTransactionAmountFormatted(record.authorization.info.amounts.original.currency, record.authorization.info.amounts.original.amount)}
          </Typography.Text>
        );
      },
    },
    {
      dataIndex: 'type',
      key: 'type',
      title: 'Tipo',
      render: (_, record) => {
        return TransactionHelper.getTransactionType(record.authorization.info.transaction.type)
      },
    },
    {
      dataIndex: 'state',
      key: 'state',
      title: 'Estado',
      render: (_, record) => {
        return ( 
          <TransactionStateTag approved={record?.authorization?.approved} irc={ record?.authorization?.irc}/>
        );
      },
    },
    {
      dataIndex: 'display_message',
      key: 'display_message',
      title: 'Mensaje',
      render: (_, record) => {
        return !record?.authorization?.approved || record?.authorization?.irc !== '8444'
          ? record?.authorization?.info?.transaction?.display_message
          : '';
      },
    },
    {
      dataIndex: 'merchant_country',
      key: 'merchant_country',
      title: 'Pais',
      render: (_, record) => {
        return record?.authorization.info.merchant.country;
      },
    },
    {
      dataIndex: 'merchant_city',
      key: 'merchant_city',
      title: 'Ciudad',
      render: (_, record) => {
        return record?.authorization.info.merchant.city;
      },
    },

    {
      dataIndex: 'actions',
      key: 'actions',
      title: 'Acciones',
      render: (_, record) => {
        return (
          <>
            <Button
              onClick={(e) => {
                e.preventDefault();
                onClickViewTransactionDetails(
                  record.authorization.info.transaction.id,
                  record.authorization.info.card.card_holder_id,
                  record.authorization.info.transaction.received_timestamp,
                  record.authorization.info.transaction.type
                );
              }}
              size="small"
              type="link"
              icon={<EyeOutlined />}
            >
              Detalle
            </Button>
          </>
        );
      },
    },
  ];

  const getCsvFile = async () => {
    if(startDate !== null && endDate !== null) {
      const response = await execute({ url: config.api.url + `/v1/reports?card_id=${cardId}&report_type=TRANSACTION&from_created_time=${startDate.format('YYYY-MM-DDT00:00:00').toString()}Z&to_created_time=${endDate.format('YYYY-MM-DDT23:59:59').toString()}Z` });

      if(response?.status === 200 && response?.data?.url !== null) {
        window.open(response?.data?.url);
        toast.success('Exito', 'Transacciones exportadas correctamente!.');
      }else {
        toast.error('Error', 'Ocurrió un problema al exportar, porfavor, intenta nuevamente.')
      }
    }
  }

  return (
    <PageWithFilters title="Transacciones">
      <PageWithFiltersContentContainer>
        <TransactionsFilters onSubmit={handleSearchClick} />
      </PageWithFiltersContentContainer>
      <PageWithFiltersContentContainer>
        <Row gutter={16} style={{ marginBottom: '1rem', textAlign: 'center' }}>
          <Col span={8}>
            <Card style={{ minHeight: '8.4rem' }}>
              <Statistic
                title="Creditos"
                value={debitsAndCredits?.credits}
                precision={2}
                decimalSeparator=","
                prefix="$"
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card style={{ minHeight: '8.4rem' }}>
              <Statistic
                title="Debitos"
                value={debitsAndCredits?.debits}
                precision={2}
                decimalSeparator=","
                prefix="$"
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card style={{ minHeight: '8.4rem' }}>
              <Statistic
                title="Disponible"
                value={balance?.data?.available}
                precision={2}
                decimalSeparator=","
                prefix="$"
              />
              <Typography.Text italic>Ultima actualizacion {getProperDate(balance?.date)}hs</Typography.Text>
            </Card>
          </Col>
        </Row>
        <CardHoldersActionsContainer>
          <ButtonExport
            disabled={ !(transactions?.length > 0) }
            loading={ fileLoading }
            title={"Descargar CSV"}
            execute={ getCsvFile } />
        </CardHoldersActionsContainer>
        <Table
          dataSource={transactions ?? []}
          scroll={{ x: true }}
          columns={columns}
          loading={isLoading}
          pagination={{
            total: pageInfo.total,
            defaultPageSize: 10,
            defaultCurrent: 1,
            onChange: handlePaginationChange,
            pageSizeOptions: ['10', '25', '50', '100'],
            showSizeChanger: true,
          }}
        />
      </PageWithFiltersContentContainer>
    </PageWithFilters>
  );
};

export default TransactionsPage;
